import { AlertMessage, ConfigurableButton } from '@/app/components'
import { Modal, Text } from '@/components'
import { useAppSelector } from '@/hooks'
import {
  ExpiredBreakdownItem,
  Transaction,
  TransactionType,
} from '@/types/transaction'
import clsx from 'clsx'
import moment from 'moment'
import { match } from 'ts-pattern'
import {
  formatRewardDate,
  isLegacyExpiration,
  pointsToDollars,
} from '../../rewards.utils'
import { formatPoints } from '@/utils/rewardPoints'
import { TransactionIcon } from './TransactionIcon'

const TRANSACTION_TYPES_WITH_DETAILS: TransactionType[] = [
  'redeem',
  'referral',
  'expire',
]

/** Whether tapping this transaction opens a detail modal */
export function hasTransactionDetails(transaction: Transaction): boolean {
  return (
    !!transaction.type &&
    TRANSACTION_TYPES_WITH_DETAILS.includes(transaction.type)
  )
}

/** The big amount, an optional caption, and the date under the modal title */
const AmountHeader = ({
  points,
  amountClassName,
  caption,
  date,
}: {
  points: number
  amountClassName: string
  caption?: string
  date: string
}) => (
  <div className="flex flex-col items-center gap-1">
    <Text variant="h1" className={amountClassName}>
      {formatPoints(points, { signed: true })}
    </Text>
    {caption && (
      <Text className="text-sm font-semibold text-gray-900">{caption}</Text>
    )}
    <Text className="text-sm font-normal text-gray-500">
      {formatRewardDate(date)}
    </Text>
  </div>
)

const BreakdownRow = ({
  type,
  label,
  date,
  points,
}: {
  type: TransactionType | null
  label: string
  date: string
  points: number
}) => (
  <div className="w-full flex flex-row items-center gap-3">
    <TransactionIcon type={type} />
    <div className="flex flex-col grow">
      <Text className="text-sm font-semibold text-gray-900">{label}</Text>
      <Text className="text-sm font-normal text-gray-500">{date}</Text>
    </div>
    <Text
      className={clsx(
        'text-sm font-bold whitespace-nowrap',
        type === 'redeem' ? 'text-green-700' : 'text-gray-900',
      )}
    >
      {formatPoints(points, { signed: true })}
    </Text>
  </div>
)

function getBreakdownLabel(item: ExpiredBreakdownItem): string {
  return match(item.type)
    .with('redeem', () => 'Redeemed')
    .with('expire', () => 'Expired')
    .otherwise(() => (item.points < 0 ? 'Adjustment' : 'Earned'))
}

const ExpiredReward = ({ transaction }: { transaction: Transaction }) => {
  const legacy = isLegacyExpiration(transaction)
  const breakdown = transaction.expired_breakdown ?? []

  return (
    <div className="flex flex-col items-center w-full gap-6">
      <AmountHeader
        points={transaction.points}
        amountClassName="text-themed-primary"
        date={transaction.created}
      />
      <div
        id="expired-breakdown"
        className="w-full flex flex-col gap-4 rounded-xl border border-solid border-gray-200 p-4"
      >
        {breakdown.map((item) => (
          <BreakdownRow
            key={item.id}
            type={item.type}
            label={getBreakdownLabel(item)}
            // The one-time legacy expiration covers points from as far back
            // as 2024, so earned dates read as month and year
            date={
              legacy && item.points > 0
                ? moment(item.created).format('MMM YYYY')
                : formatRewardDate(item.created)
            }
            points={item.points}
          />
        ))}
        <div
          className={clsx('w-full', {
            'border-t border-solid border-gray-200 pt-4': breakdown.length,
          })}
        >
          <BreakdownRow
            type="expire"
            label="Expired"
            date={formatRewardDate(transaction.created)}
            points={transaction.points}
          />
        </div>
      </div>
      <AlertMessage variant="info" className="w-full">
        {legacy
          ? `Points earned before our rewards terms changed expired on ${formatRewardDate(
              transaction.created,
            )}. Points now expire 1 year after they're earned.`
          : "Points expire 1 year after they're earned. These weren't redeemed in time."}
      </AlertMessage>
    </div>
  )
}

const InvitationBonus = ({ transaction }: { transaction: Transaction }) => (
  <AmountHeader
    points={Math.abs(transaction.points)}
    amountClassName="text-themed-primary"
    caption={`For inviting ${transaction.referral.referred_name}`}
    date={transaction.created}
  />
)

const CashBack = ({ transaction }: { transaction: Transaction }) => {
  const user = useAppSelector((state) => state.user.user)
  return (
    <div className="flex flex-col items-center w-full gap-6">
      <AmountHeader
        points={-Math.abs(transaction.points)}
        amountClassName="text-green-700"
        // The payout itself is still cash, so it stays in dollars
        caption={`Redeemed for $${pointsToDollars(
          Math.abs(transaction.points),
        )}`}
        date={transaction.created}
      />
      <AlertMessage variant="info" className="w-full">
        We will be sending your reward to <strong>{user?.username}</strong>.
        <br />
        <br />
        Please allow 10-14 business days for your reward to arrive.
      </AlertMessage>
    </div>
  )
}

export default function TransactionDisplayModal({
  transaction,
  onClose,
}: {
  transaction: Transaction | null
  onClose: () => void
}) {
  const title = match(transaction?.type)
    .with('redeem', () => 'Reward redemption')
    .with('referral', () => 'Invitation Bonus')
    .with('expire', () => 'Expired reward')
    .otherwise(() => undefined)

  return (
    <Modal
      id="transactionDisplayModal"
      open={!!transaction && hasTransactionDetails(transaction)}
      onClose={onClose}
      title={title}
      className="md:!max-w-[400px] max-h-[90vh]"
    >
      <div className="flex flex-col items-center w-full gap-6">
        {transaction &&
          match(transaction.type)
            .with('redeem', () => <CashBack transaction={transaction} />)
            .with('referral', () => (
              <InvitationBonus transaction={transaction} />
            ))
            .with('expire', () => <ExpiredReward transaction={transaction} />)
            .otherwise(() => null)}
        <ConfigurableButton
          id="transactionDisplayModalDone"
          className="w-full"
          onClick={onClose}
        >
          Done
        </ConfigurableButton>
      </div>
    </Modal>
  )
}
